import { Modal } from "@mantine/core";
import { useEffect, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { LoadOrError } from "../../../../../components/LoadOrError/LoadOrError";
import { useParagraph6GemeindeAnteilErzeuger } from "../../../hooks/useParagraph6GemeindeAnteilErzeuger";
import { useParagraph6GemeindeAnteilErzeugerMutations } from "../../../hooks/useParagraph6GemeindeAnteilErzeugerMutations";
import { useParagraph6UnassignedGenerators } from "../../../hooks/useParagraph6UnassignedGenerators";
import type { Paragraph6GemeindeAnteilErzeugerPayload } from "../../../Paragraph6.types";
import { Paragraph6GeneratorForm } from "./Paragraph6GeneratorForm/Paragraph6GeneratorForm";

interface Paragraph6GeneratorModalProps {
  variantId: number;
  projectId: string;
  contractId: string;
  generatorShareId?: number;
  isOpen?: boolean;
  onClose: () => void;
}

function Paragraph6GeneratorModal({
  variantId,
  projectId,
  contractId,
  generatorShareId,
  isOpen,
  onClose
}: Paragraph6GeneratorModalProps) {
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [responseError, setResponseError] = useState<Error>();

  const editMode = !!generatorShareId;

  const defaultParagraph6GemeindeAnteilErzeuger = {
    contract: contractId
  } as const satisfies Pick<
    Paragraph6GemeindeAnteilErzeugerPayload,
    "contract"
  >;

  const {
    paragraph6GemeindeAnteilErzeuger,
    isFetching: paragraph6GemeindeAnteilErzeugerFetching
  } = useParagraph6GemeindeAnteilErzeuger(contractId, generatorShareId, {
    refetchOnWindowFocus: false
  });
  const {
    paragraph6GemeindeAnteilErzeugerCreateMutation,
    paragraph6GemeindeAnteilErzeugerEditMutation
  } = useParagraph6GemeindeAnteilErzeugerMutations(variantId, contractId);
  const isMutating =
    paragraph6GemeindeAnteilErzeugerCreateMutation.isPending ||
    paragraph6GemeindeAnteilErzeugerEditMutation.isPending;

  const {
    paragraph6UnassignedGenerators,
    isLoading: paragraph6UnassignedGeneratorsLoading
  } = useParagraph6UnassignedGenerators(contractId);

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  const onSubmit: SubmitHandler<Paragraph6GemeindeAnteilErzeugerPayload> = (
    data
  ) => {
    const mutation = editMode
      ? paragraph6GemeindeAnteilErzeugerEditMutation
      : paragraph6GemeindeAnteilErzeugerCreateMutation;

    mutation.mutate(data, {
      onSuccess: () => {
        onClose();
      },
      onError: (error) => {
        setResponseError(error);
      }
    });
  };

  return (
    <Modal
      opened={isModalOpen || false}
      size="min(80vw, 1200px)"
      title={`Erzeuger ${editMode ? "bearbeiten" : "hinzufügen"}`}
      onClose={onClose}
    >
      <LoadOrError
        loading={
          (editMode &&
            (paragraph6GemeindeAnteilErzeugerFetching ||
              paragraph6UnassignedGeneratorsLoading)) ||
          paragraph6UnassignedGeneratorsLoading
        }
        loadingMessage="Erzeuger wird geladen ..."
      >
        <Paragraph6GeneratorForm
          editMode={editMode}
          isSubmitting={isMutating}
          paragraph6GemeindeAnteilErzeuger={
            paragraph6GemeindeAnteilErzeuger ||
            defaultParagraph6GemeindeAnteilErzeuger
          }
          paragraph6UnassignedGenerators={paragraph6UnassignedGenerators}
          projectId={projectId}
          submissionError={responseError}
          onCancel={onClose}
          onSubmit={onSubmit}
        />
      </LoadOrError>
    </Modal>
  );
}

export { Paragraph6GeneratorModal, Paragraph6GeneratorModalProps };
